// What every AI chat and AI grade on the site tells the user.
//
//   - AiFirstUseNotice: shown above an AI chat until the person dismisses it
//     once (per browser). Says they are talking to an AI, that what they type
//     goes to the AI provider, not to share sensitive information, and that AI
//     can be wrong. Links to the Terms and the Privacy Policy (new tab, so the
//     chat stays put).
//   - AiMistakesNote: a short permanent line under every AI chat box. Anthropic's
//     Commercial Terms require telling users not to rely on AI output.
//   - AiGradedNote: under every AI grade, says it was graded by AI and gives a
//     way to ask a person to look at it.
//
// Wording is in translations/{en,es}/common.json under "aiNotice".
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import "./AiNotice.css";

const STORAGE_KEY = "codenec.aiNotice.v1";

const seenBefore = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "seen";
  } catch {
    return false; // storage blocked: show it, it can still be dismissed
  }
};

// The first control after `el` in page order (normally the chat box right
// below the notice), so dismissing does not drop keyboard focus to <body>.
const nextFocusable = (el) => {
  if (!el) return null;
  const candidates = document.querySelectorAll(
    'textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), button:not([disabled]), a[href]'
  );
  for (const c of candidates) {
    if (!el.contains(c) && el.compareDocumentPosition(c) & Node.DOCUMENT_POSITION_FOLLOWING) return c;
  }
  return null;
};

export function AiFirstUseNotice({ voice = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(() => !seenBefore());
  const boxRef = useRef(null);
  if (!open) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "seen");
    } catch {
      // Not remembered on this device; it simply shows again next time.
    }
    const next = nextFocusable(boxRef.current);
    setOpen(false);
    if (next) setTimeout(() => next.focus(), 0);
  };

  return (
    <div className="ai-first-notice" role="note" ref={boxRef}>
      <p className="ai-first-notice__title">{t("aiNotice.firstTitle")}</p>
      <p className="ai-first-notice__body">{t("aiNotice.firstBody")}</p>
      {voice && <p className="ai-first-notice__body">{t("aiNotice.firstVoice")}</p>}
      <p className="ai-first-notice__body">{t("aiNotice.firstLegalBefore")} <a href="/terms" target="_blank" rel="noopener noreferrer">{t("legal.links.terms")}</a> {t("legal.andThe")} <a href="/privacy" target="_blank" rel="noopener noreferrer">{t("legal.links.privacy")}</a>.</p>
      <button type="button" className="ai-first-notice__ok" onClick={dismiss}>
        {t("aiNotice.firstOk")}
      </button>
    </div>
  );
}

export function AiMistakesNote() {
  const { t } = useTranslation();
  return <p className="ai-mistakes-note">{t("aiNotice.mistakes")}</p>;
}

export function AiGradedNote() {
  const { t } = useTranslation();
  return (
    <p className="ai-graded-note">
      {/* A new tab, so the grade the student wants reviewed stays on screen. */}
      {t("aiNotice.graded")}{" "}
      <a href="/contactus" target="_blank" rel="noopener noreferrer">{t("aiNotice.gradedContact")}</a>
    </p>
  );
}
